import { observer } from "mobx-react-lite";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { Columns } from "./Columns";
import { blockage } from "@/endpoints/dryp/blockage";
import { CUSTOM_FILTERS } from "@/config/list";
import { StatusTypeFilter } from "./StatusTypeFilter";
import { ExpandedBlockagePanel } from "./ExpandedBlockagePanel";
import { getTableStructureFilterParams } from "@/utils/filters";
import { StructureFilters } from "@/components/filters/StructureFilters";
import { ListMapView } from "@/components/ListMapView";
import { useMapStore } from "@/hooks/useMapStore";
import { MarkerDataConfig, MarkerLayerConfig } from "@/components/Map/stores/MapStore";
import { SearchInput } from "@/components/SearchInput";
import { BlockagePopUp } from "./BlockagePopUp.";
import { BlockageSummaryMetrics } from "@/components/Blockages/SummaryMetrics";
import { BLOCKAGE_STATUS_COLORS } from "@/config/blockageStatus";
import { FeatureStatusBadge } from "@/components/FeatureStatusBadge";

export const blockageLayerConfig: MarkerLayerConfig = {
	paint: {
		"circle-color": [
			"match",
			["get", "status"],
			...Object.entries(BLOCKAGE_STATUS_COLORS)
				.filter(([k]) => k !== "default")
				.flat(),
			BLOCKAGE_STATUS_COLORS.default,
		],
		"circle-radius": 6,
	},
};

export const blockageDataConfig: MarkerDataConfig<Dryp.Blockage> = {
	getCoordinates: b => [b.coordinate.longitude, b.coordinate.latitude],
	getId: b => b.id,
	getProperties: b => ({
		status: b.status.status,
		dataQuality: b.dataQuality,
	}),
};

export const Blockages = observer(() => {
	const blockagesApi = useAsyncStateStore(blockage.getAll, { debounce: 300 });
	const mapBlockagesApi = useAsyncStateStore(blockage.getAll, { debounce: 300 });

	const { table } = useServerTable({
		columns: Columns,
		data: blockagesApi.data?.items || [],
		fetchFn: params => {
			const structureFilters = getTableStructureFilterParams(params.filters);
			const status = params.filters?.find(f => f.id === CUSTOM_FILTERS.status)?.value;
			return blockagesApi.fetch(
				{ pageNumber: params.page, pageSize: params.pageSize },
				params.sort,
				structureFilters,
				status,
			);
		},
		initialValues: {
			page: 0,
			pageSize: 10,
			sorting: [{ id: "updatedAt", desc: true }],
			columnVisibility: {
				structureId: false,
				updatedAt: false,
				dataQuality: false,
			},
		},
		enableUrlParams: true,
		initFromUrl: true,
	});

	const mapStore = useMapStore(blockageLayerConfig, blockageDataConfig);

	mapStore.onFetch = async () => {
		const structureFilters = getTableStructureFilterParams(mapStore.filters.getFilters());

		const status = mapStore.filters.toJS.find(f => f.id === CUSTOM_FILTERS.status)?.value as
			| Dryp.BlockageStatus
			| undefined;
		await mapBlockagesApi.fetch(
			{
				pageNumber: 0,
				pageSize: 100000,
			},
			undefined,
			structureFilters,
			status,
		);
		if (!mapStore.isLoaded) return;
		mapStore.setMarkers(mapBlockagesApi.data?.items || []);
		mapStore.fitToData(mapBlockagesApi.data?.items || [], s => [s.coordinate.longitude, s.coordinate.latitude]);
	};

	return (
		<div className="flex flex-col gap-4 p-4 md:p-6 min-h-full overflow-hidden">
			<header className="flex flex-col items-start gap-2">
				<h1 className="text-2xl md:text-4xl font-semibold tracking-tight">Blockages</h1>
				<FeatureStatusBadge featureName="Blockages" status="research-preview" />
			</header>
			<BlockageSummaryMetrics />
			<ListMapView
				filters={(filter, _view) => (
					<div className="flex flex-wrap md:flex-nowrap gap-2 items-end">
						<SearchInput
							value={(filter.getFilter(CUSTOM_FILTERS.search) as string | undefined) ?? ""}
							onChange={value => filter.setFilter(CUSTOM_FILTERS.search, value)}
						/>
						<StatusTypeFilter filters={filter} />
						<StructureFilters filter={filter} hideNetworkType hideTypes />
					</div>
				)}
				list={{
					table,
					expandedRowPanel: row => <ExpandedBlockagePanel row={row} />,
					pagination: {
						total: blockagesApi.data?.query?.pagination.totalItems || 0,
					},
					layout: {
						loading: blockagesApi.loading,
					},
				}}
				enableUrlParams={true}
				initFromUrl={true}
				map={{
					mapStore,
					loader: {
						isLoading: mapBlockagesApi.loading,
					},
					enableUrlParams: true,
					initFromUrl: true,
					renderPopup: blockage => <BlockagePopUp blockage={blockage} />,
				}}
			/>
		</div>
	);
});
